import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DownloadIcon from "@mui/icons-material/Download";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { Coachee, Session } from "../../models";
import { SessionService } from "../../services/coachingNavigator/sessionService";
import { CoacheeService } from "../../services/coachingNavigator/coacheeService";
import { MethodService } from "../../services/coachingNavigator/methodService";
import config from "../../config";
import { formatHoursDisplay } from "../../util/formatHours";
import { Context } from "../../components/context";
import { generateSessionOverviewPdf } from "../../util/sessionOverviewPdfUtil";

type SessionData = {
  id: string;
  name: string;
  clientId?: string;
  startTime?: Date | string;
  endTime?: Date | string;
  location?: string;
  sessionRemarks: string;
  clientObjectives: string;
  keyInsights: string;
  methodsCovered: string[];
};

type SessionCounts = {
  booked: number;
  completed: number;
  remaining: number;
};

type HoursCounts = {
  booked: number;
  done: number;
  remaining: number;
};

type ClientData = {
  id?: string;
  name: string;
  role?: string;
  company?: string;
  sessionCounts?: SessionCounts;
  hoursCounts?: HoursCounts;
  email?: string;
  nextSession?: string;
  avatarInitials: string;
};

const sessionService = new SessionService(config.api.baseUrl, "/sessions");
const coacheeService = new CoacheeService(config.api.baseUrl, "/coachee");
const methodService = new MethodService(config.api.baseUrl, "/method");

const formatDate = (value?: Date | string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date
    .toLocaleDateString(i18n.language || undefined, {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    })
    .replace(/\//g, ".");
};

const formatTime = (value?: Date | string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const hh = date.getHours().toString().padStart(2, "0");
  const mm = date.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
};

const methodKey = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  // Normalize ObjectId-like objects or other primitives
  if (
    typeof value === "object" &&
    "toString" in value &&
    typeof (value as { toString: () => string }).toString === "function"
  ) {
    return (value as { toString: () => string }).toString();
  }
  return String(value);
};

const AutoGrowTextarea: React.FC<{
  value: string;
  onChange: (val: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
}> = ({ value, onChange, onBlur, placeholder, minRows = 2, maxRows = 6 }) => {
  return (
    <TextField
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      multiline
      minRows={minRows}
      maxRows={maxRows}
      fullWidth
      size="small"
      sx={{
        backgroundColor: "white",
      }}
    />
  );
};

const CountChips: React.FC<{
  counts: {
    value: number;
    labelKey: string;
    color: "primary" | "success" | "warning";
  }[];
  formatter?: (n: number) => string;
}> = ({ counts, formatter }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
      {counts.map(({ value, labelKey, color }) => (
        <Chip
          key={labelKey}
          label={`${formatter ? formatter(value) : value} ${t(labelKey)}`}
          size="small"
          color={color}
          variant="outlined"
          sx={{ fontWeight: 500, fontSize: "0.75rem" }}
        />
      ))}
    </Box>
  );
};

const ClientInfoSection: React.FC<{
  client: ClientData;
  onClientDetail?: () => void;
}> = ({ client, onClientDetail }) => {
  const { t } = useTranslation();
  const rows: { label: string; value: React.ReactNode }[] = [
    {
      label: t("coachingNavigator.clientOverview.role"),
      value: client.role || "—",
    },
    {
      label: t("coachingNavigator.coachingTime"),
      value: client.hoursCounts ? (
        <CountChips
          counts={[
            {
              value: client.hoursCounts.booked,
              labelKey: "coachingNavigator.booked",
              color: "primary",
            },
            {
              value: client.hoursCounts.done,
              labelKey: "coachingNavigator.done",
              color: "success",
            },
            {
              value: client.hoursCounts.remaining,
              labelKey: "coachingNavigator.remaining",
              color: "warning",
            },
          ]}
          formatter={formatHoursDisplay}
        />
      ) : (
        "—"
      ),
    },
    {
      label: t("coachingNavigator.clientOverview.company"),
      value: client.company || "—",
    },
    {
      label: t("coachingNavigator.sessions"),
      value: client.sessionCounts ? (
        <CountChips
          counts={[
            {
              value: client.sessionCounts.booked,
              labelKey: "coachingNavigator.booked",
              color: "primary",
            },
            {
              value: client.sessionCounts.completed,
              labelKey: "coachingNavigator.completed",
              color: "success",
            },
            {
              value: client.sessionCounts.remaining,
              labelKey: "coachingNavigator.remaining",
              color: "warning",
            },
          ]}
        />
      ) : (
        "—"
      ),
    },
    { label: t("coachingNavigator.email"), value: client.email || "—" },
    {
      label: t("coachingNavigator.nextSession"),
      value: client.nextSession || "—",
    },
  ];
  return (
    <Paper sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: "#e5e7eb",
              color: "text.primary",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            {client.avatarInitials}
          </Avatar>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
            }}
          >
            {client.name}
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={onClientDetail}>
          {t("coachingNavigator.clientDetails")}
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        {rows.map(({ label, value }) => (
          <Box
            key={label}
            sx={{ display: "flex", gap: 1, alignItems: "center" }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                minWidth: 110,
                flexShrink: 0,
              }}
            >
              {label}
            </Typography>
            {typeof value === "string" ? (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                {value}
              </Typography>
            ) : (
              value
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const UpcomingSessionCard: React.FC<{
  session: SessionData;
  onUpdate: (id: string, field: keyof SessionData, value: string) => void;
  onSave: (id: string) => void;
  onOpenSession?: (id: string) => void;
}> = ({ session, onUpdate, onSave, onOpenSession }) => {
  const { t } = useTranslation();
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
            }}
          >
            {session.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {formatDate(session.startTime)} {formatTime(session.startTime)}–
            {formatTime(session.endTime)}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          sx={{ flexShrink: 0 }}
          onClick={() => onOpenSession?.(session.id)}
        >
          {t("coachingNavigator.sessionOverviewPage.sessionDetail")}
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 0.5,
            }}
          >
            {t("coachingNavigator.sessionDetails.sessionRemarks")}
          </Typography>
          <AutoGrowTextarea
            value={session.sessionRemarks}
            onChange={(val) => onUpdate(session.id, "sessionRemarks", val)}
            onBlur={() => onSave(session.id)}
            placeholder={t(
              "coachingNavigator.sessionDetails.sessionRemarksPlaceholder",
            )}
            minRows={2}
            maxRows={6}
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 0.5,
            }}
          >
            {t("coachingNavigator.sessionDetails.clientObjective")}
          </Typography>
          <AutoGrowTextarea
            value={session.clientObjectives}
            onChange={(val) => onUpdate(session.id, "clientObjectives", val)}
            onBlur={() => onSave(session.id)}
            placeholder={t(
              "coachingNavigator.sessionDetails.clientObjectivePlaceholder",
            )}
            minRows={2}
            maxRows={6}
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 0.5,
            }}
          >
            {t("coachingNavigator.sessionDetails.keyInsights")}
          </Typography>
          <AutoGrowTextarea
            value={session.keyInsights}
            onChange={(val) => onUpdate(session.id, "keyInsights", val)}
            onBlur={() => onSave(session.id)}
            placeholder={t(
              "coachingNavigator.sessionDetails.keyInsightsPlaceholder",
            )}
            minRows={1}
            maxRows={4}
          />
        </Box>
      </Box>
    </Paper>
  );
};

const UpcomingSessionsPanel: React.FC<{
  sessions: SessionData[];
  offset: number;
  onOffsetChange: (offset: number) => void;
  onUpdate: (id: string, field: keyof SessionData, value: string) => void;
  onSave: (id: string) => void;
  onOpenSession?: (id: string) => void;
}> = ({ sessions, offset, onOffsetChange, onUpdate, onSave, onOpenSession }) => {
  const { t } = useTranslation();
  const visible = sessions.slice(offset, offset + 2);
  const canGoBack = offset > 0;
  const canGoForward = offset + 2 < sessions.length;

  return (
    <Paper sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: "text.secondary",
          }}
        >
          {t("coachingNavigator.upcomingSessions").toUpperCase()}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            disabled={!canGoBack}
            onClick={() => onOffsetChange(Math.max(0, offset - 1))}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={!canGoForward}
            onClick={() => onOffsetChange(offset + 1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {visible.length > 0 ? (
          visible.map((session) => (
            <UpcomingSessionCard
              key={session.id}
              session={session}
              onUpdate={onUpdate}
              onSave={onSave}
              onOpenSession={onOpenSession}
            />
          ))
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t("coachingNavigator.sessionOverviewPage.noUpcomingSessions")}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// For simplicity (atleast for now), this component currently just shows a truncated text box without actual expand/collapse functionality.
const ExpandableText: React.FC<{ text: string; label: string }> = ({
  text,
  label,
}) => {
  // const [expanded, setExpanded] = useState(false);
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        {/* <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ color: "text.secondary" }}
        >
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton> */}
      </Box>
      <Box
        sx={{
          fontSize: "0.875rem",
          bgcolor: "grey.50",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 1.5,
          py: 1,
          overflow: "hidden",
          maxHeight: 132,
        }}
      >
        <Typography variant="body2">{text || "—"}</Typography>
      </Box>
    </Box>
  );
};

const RecentSessionCard: React.FC<{
  session: SessionData;
  onOpenSession?: (id: string) => void;
}> = ({ session, onOpenSession }) => {
  const { t } = useTranslation();
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
            }}
          >
            {session.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {formatDate(session.startTime)} {formatTime(session.startTime)}–
            {formatTime(session.endTime)}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          sx={{ flexShrink: 0 }}
          onClick={() => onOpenSession?.(session.id)}
        >
          {t("coachingNavigator.sessionOverviewPage.sessionDetail")}
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 1,
            }}
          >
            {t("coachingNavigator.sessionDetails.methodsCovered")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {session.methodsCovered.length > 0 ? (
              session.methodsCovered.map((method) => (
                <Chip
                  key={method}
                  label={method}
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    bgcolor: "#1E3A8A",
                    color: "#fff",
                  }}
                />
              ))
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("coachingNavigator.sessionDetails.noMethods")}
              </Typography>
            )}
          </Box>
        </Box>
        <ExpandableText
          label={t("coachingNavigator.sessionDetails.clientObjective")}
          text={session.clientObjectives}
        />
        <ExpandableText
          label={t("coachingNavigator.sessionDetails.keyInsights")}
          text={session.keyInsights}
        />
      </Box>
    </Paper>
  );
};

const RecentSessionsPanel: React.FC<{
  sessions: SessionData[];
  offset: number;
  onOffsetChange: (offset: number) => void;
  onOpenSession?: (id: string) => void;
}> = ({ sessions, offset, onOffsetChange, onOpenSession }) => {
  const { t } = useTranslation();
  const currentSession = sessions[offset];
  const canGoBack = offset + 1 < sessions.length;
  const canGoForward = offset > 0;

  return (
    <Paper sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: "text.secondary",
          }}
        >
          {t(
            "coachingNavigator.sessionOverviewPage.recentSessions",
          ).toUpperCase()}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            disabled={!canGoBack}
            onClick={() => onOffsetChange(offset + 1)}
            aria-label="Older sessions"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={!canGoForward}
            onClick={() => onOffsetChange(offset - 1)}
            aria-label="More recent sessions"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      {currentSession ? (
        <RecentSessionCard
          session={currentSession}
          onOpenSession={onOpenSession}
        />
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {t("coachingNavigator.sessionOverviewPage.noRecentSessions")}
        </Typography>
      )}
    </Paper>
  );
};

const SessionOverviewPage: React.FC = () => {
  const { clientId } = useParams<{ clientId?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appContext = React.useContext(Context);
  const creatorEmail = React.useMemo(
    () => (appContext.state.invoker?.email || "").trim(),
    [appContext.state.invoker?.email],
  );
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const rawSessionsRef = useRef<Map<string, Session>>(new Map());
  const saveQueuesRef = useRef<Map<string, Promise<void>>>(new Map());
  const [client, setClient] = useState<ClientData | null>(null);
  const [upcomingOffset, setUpcomingOffset] = useState(0);
  const [recentOffset, setRecentOffset] = useState(0);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [saveError, setSaveError] = useState(false);

  const now = useMemo(() => new Date(), []);

  const mapSession = (
    session: Session,
    methodMap: Record<string, string>,
  ): SessionData => ({
    id: session.id || "",
    name: session.name,
    clientId: session.clientId,
    startTime: session.startTime,
    endTime: session.endTime,
    location: session.location,
    sessionRemarks: (session.remarks || []).join("\n"),
    clientObjectives: session.objective || "",
    keyInsights: session.keyInsights || "",
    methodsCovered: (session.methodsUsed || []).map((m) => {
      const key = methodKey(m);
      return methodMap[key] || key || "—";
    }),
  });

  const loadData = useCallback(
    async (methodMap: Record<string, string>) => {
      try {
        let nextSessionText: string | undefined;
        let coacheeData: Coachee | null = null;
        if (clientId) {
          try {
            const next = await sessionService.getNextForClient(clientId);
            if (next?.startTime || (next as unknown as { date?: Date }).date) {
              const start =
                next.startTime ?? (next as unknown as { date?: Date }).date;
              nextSessionText = `${formatDate(start)} ${formatTime(start)}`;
            }
          } catch (err) {
            console.error("Failed to load next session", err);
          }
        }
        if (clientId) {
          const coachee = await coacheeService.get(clientId);
          if (coachee) {
            coacheeData = coachee;
          }
        }

        if (!creatorEmail) {
          setSessions([]);
          rawSessionsRef.current = new Map();
          return;
        }

        const allSessions = await sessionService.getByCreator(creatorEmail);
        const filtered = (allSessions || []).filter(
          (s) => s.id && (!clientId || s.clientId === clientId),
        );
        rawSessionsRef.current = new Map(filtered.map((s) => [s.id as string, s]));
        const mapped = filtered.map((s) => mapSession(s, methodMap));
        setSessions(mapped);

        if (coacheeData) {
          const safeBookedSessions = Number.isFinite(coacheeData.bookedSessions)
            ? Math.max(Number(coacheeData.bookedSessions), 0)
            : 0;
          const safeCompletedSessions = Number.isFinite(
            coacheeData.completedSessions,
          )
            ? Math.max(Number(coacheeData.completedSessions), 0)
            : 0;
          const coacheeNext = coacheeData.nextSession
            ? `${formatDate(coacheeData.nextSession)} ${formatTime(coacheeData.nextSession)}`
            : undefined;
          setClient({
            id: coacheeData.id?.toString() || clientId,
            name: coacheeData.name,
            role: coacheeData.role,
            company: coacheeData.company,
            sessionCounts: {
              booked: safeBookedSessions,
              completed: safeCompletedSessions,
              remaining: Math.max(
                safeBookedSessions - safeCompletedSessions,
                0,
              ),
            },
            hoursCounts: (() => {
              const safeBooked = Number.isFinite(coacheeData.bookedHours)
                ? Math.max(Number(coacheeData.bookedHours), 0)
                : 0;
              const safeDone = Number.isFinite(coacheeData.completedHours)
                ? Math.max(Number(coacheeData.completedHours), 0)
                : 0;
              return {
                booked: safeBooked,
                done: safeDone,
                remaining: Math.max(safeBooked - safeDone, 0),
              };
            })(),
            email: coacheeData.email,
            nextSession: nextSessionText || coacheeNext,
            avatarInitials: (coacheeData.name || "C")
              .split(" ")
              .map((p) => p[0])
              .join("")
              .slice(0, 2)
              .toUpperCase(),
          });
        }
      } catch (err) {
        console.error("Failed to load session overview data", err);
      } finally {
        // no-op
      }
    },
    [clientId, creatorEmail],
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const map: Record<string, string> = {};
      try {
        if (creatorEmail) {
          const methods = await methodService.getByCreator(creatorEmail);
          if (cancelled) return;
          (methods || []).forEach((m) => {
            if (!m) return;
            const key = methodKey(
              (m as { id?: string; _id?: string }).id ??
                (m as { _id?: string })._id,
            );
            if (key) {
              map[key] = (m as { name?: string }).name || key;
            }
          });
        }
      } catch (err) {
        console.error("Failed to load methods", err);
      }

      if (!cancelled) {
        await loadData(map);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [clientId, creatorEmail, loadData]);

  const upcomingSessions = useMemo(
    () =>
      sessions
        .filter((s) => {
          const date = s.startTime ? new Date(s.startTime) : null;
          return date && !Number.isNaN(date.getTime()) && date > now;
        })
        .sort(
          (a, b) =>
            new Date(a.startTime || "").getTime() -
            new Date(b.startTime || "").getTime(),
        ),
    [sessions, now],
  );

  const recentSessions = useMemo(
    () =>
      sessions
        .filter((s) => {
          const date = s.startTime ? new Date(s.startTime) : null;
          return date && !Number.isNaN(date.getTime()) && date <= now;
        })
        .sort(
          (a, b) =>
            new Date(b.startTime || "").getTime() -
            new Date(a.startTime || "").getTime(),
        ),
    [sessions, now],
  );

  const handleOpenSession = useCallback(
    async (id: string) => {
      const pending = saveQueuesRef.current.get(id);
      if (pending) {
        try {
          await pending;
        } catch {
          return;
        }
      }
      navigate(`/sessions/${id}`, {
        state: { returnTo: `/sessionOverview/${clientId}` },
      });
    },
    [clientId, navigate],
  );

  const buildSessionOverviewPdf = useCallback(
    async (mode: "download" | "show") => {
      setExportAnchorEl(null);
      if (!client) return;
      try {
        await generateSessionOverviewPdf(
          { name: client.name },
          sessions.map((s) => ({
            startTime: s.startTime ?? new Date(),
            endTime: s.endTime ?? s.startTime ?? new Date(),
            location: s.location,
            name: s.name,
            objective: s.clientObjectives,
          })),
          mode,
        );
      } catch (err) {
        console.error("Failed to render session overview PDF", err);
      }
    },
    [client, sessions],
  );

  const handleExportPDF = useCallback(
    () => buildSessionOverviewPdf("download"),
    [buildSessionOverviewPdf],
  );

  const handleShowPDF = useCallback(
    () => buildSessionOverviewPdf("show"),
    [buildSessionOverviewPdf],
  );

  const handleUpdateSession = useCallback(
    (id: string, field: keyof SessionData, value: string) => {
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
      );
      const raw = rawSessionsRef.current.get(id);
      if (!raw) return;
      const next: Session = { ...raw };
      if (field === "sessionRemarks") {
        next.remarks = value
          ? value.split("\n").filter((v) => v.trim() !== "")
          : [];
      } else if (field === "clientObjectives") {
        next.objective = value;
      } else if (field === "keyInsights") {
        next.keyInsights = value;
      }
      rawSessionsRef.current.set(id, next);
    },
    [],
  );

  const handleSaveSession = useCallback((id: string) => {
    const prev = saveQueuesRef.current.get(id) ?? Promise.resolve();
    const next = prev
      .catch(() => undefined)
      .then(async () => {
        const raw = rawSessionsRef.current.get(id);
        if (!raw) return;
        const patch: Partial<Session> = {
          remarks: raw.remarks,
          objective: raw.objective,
          keyInsights: raw.keyInsights,
        };
        try {
          await sessionService.patch(id, patch);
        } catch (err) {
          console.error("Failed to save session", err);
          setSaveError(true);
          throw err;
        }
      });
    saveQueuesRef.current.set(id, next);
    return next;
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 3, bgcolor: "background.paper" }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Action bar */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 1,
            px: 3,
            py: 2,
          }}
        >
          <IconButton
            onClick={() => navigate("/sessions")}
            sx={{ color: "text.secondary" }}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              pointerEvents: "none",
            }}
          >
            {t("coachingNavigator.sessionOverview")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
              disabled={!client}
            >
              {t("coachingNavigator.clientOverview.export")}
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem onClick={handleExportPDF}>
                <PdfIcon sx={{ mr: 1 }} fontSize="small" />
                {t("coachingNavigator.clientOverview.exportPDF")}
              </MenuItem>
              <MenuItem onClick={handleShowPDF}>
                <PdfIcon sx={{ mr: 1 }} fontSize="small" />
                {t("coachingNavigator.clientOverview.showPDF")}
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {client && (
          <ClientInfoSection
            client={client}
            onClientDetail={() =>
              client.id && navigate(`/coaching-hub/${client.id}`)
            }
          />
        )}

        <UpcomingSessionsPanel
          sessions={upcomingSessions}
          offset={upcomingOffset}
          onOffsetChange={setUpcomingOffset}
          onUpdate={handleUpdateSession}
          onSave={handleSaveSession}
          onOpenSession={handleOpenSession}
        />

        <RecentSessionsPanel
          sessions={recentSessions}
          offset={recentOffset}
          onOffsetChange={setRecentOffset}
          onOpenSession={handleOpenSession}
        />
      </Box>
      <Snackbar
        open={saveError}
        autoHideDuration={4000}
        onClose={() => setSaveError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSaveError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {t("coachingNavigator.sessionOverviewPage.saveError")}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SessionOverviewPage;
